// pages/ChatSpace/ChatSpaceSidebar.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    Avatar,
    Drawer,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import axiosInstance from '../../utils/axiosInstance';

export default function ChatSpaceSidebar({
    isOpen,
    isCollapsed = false,
    onToggle,
    onConversationSelect,
    currentSessionCode,
    isMobile = false,
}) {
    const [conversations, setConversations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState('');

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const fname = user?.profile?.fname || '';
    const lname = user?.profile?.lname || '';
    const fullName = `${fname} ${lname}`.trim();

    useEffect(() => {
        if (isOpen) loadConversations();
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && currentSessionCode) loadConversations();
    }, [currentSessionCode]);

    const loadConversations = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/chatspace/conversations');
            if (response.data.success) {
                setConversations(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNewChat = () => {
        onConversationSelect(null);
        if (isMobile) onToggle();
    };

    const handleSelect = (sessionCode) => {
        onConversationSelect(sessionCode);
        if (isMobile) onToggle();
    };

    const handleDelete = async (sessionCode, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this conversation?')) return;
        try {
            const resp = await axiosInstance.delete(`/api/chatspace/conversations/${sessionCode}`);
            if (resp.data.success) {
                setConversations((prev) => prev.filter((c) => c.session_code !== sessionCode));
                if (sessionCode === currentSessionCode) handleNewChat();
            }
        } catch (error) {
            console.error('Failed to delete conversation:', error);
        }
    };

    const startEditing = (conv, e) => {
        e.stopPropagation();
        setEditingId(conv.session_code);
        setEditTitle(conv.title);
    };

    const handleRename = async (sessionCode) => {
        if (!editTitle.trim()) return;
        try {
            const resp = await axiosInstance.patch(`/api/chatspace/conversations/${sessionCode}/title`, {
                title: editTitle.trim(),
            });
            if (resp.data.success) {
                setConversations((prev) =>
                    prev.map((c) =>
                        c.session_code === sessionCode ? { ...c, title: editTitle.trim() } : c
                    )
                );
                setEditingId(null);
            }
        } catch (error) {
            console.error('Failed to rename:', error);
        }
    };

    const filtered = conversations.filter((c) =>
        c.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const mins = Math.floor(diff / 60000);
        const hrs = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        if (mins < 60) return `${mins}m ago`;
        if (hrs < 24) return `${hrs}h ago`;
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
    };

    const sidebarContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                {!isCollapsed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight={600}>Chats</Typography>
                    </Box>
                )}
                <IconButton size="small" onClick={onToggle} title={isCollapsed ? 'Expand' : 'Collapse'}>
                    <MenuOpenIcon
                        sx={{
                            fontSize: 18,
                            color: 'grey.500',
                            transform: isCollapsed ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.3s',
                        }}
                    />
                </IconButton>
            </Box>

            {/* New Chat */}
            <Box sx={{ p: 1.5, px: isCollapsed ? 1 : 1.5 }}>
                {isCollapsed ? (
                    <IconButton
                        onClick={handleNewChat}
                        color="primary"
                        sx={{
                            width: '100%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'primary.dark' },
                        }}
                    >
                        <AddIcon />
                    </IconButton>
                ) : (
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleNewChat}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                    >
                        New Chat
                    </Button>
                )}
            </Box>

            {/* Search */}
            {!isCollapsed && (
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                                </InputAdornment>
                            ),
                            sx: { fontSize: '0.75rem', borderRadius: 2 },
                        }}
                    />
                </Box>
            )}

            {/* Conversations */}
            <Box sx={{ flex: 1, overflow: 'auto', px: isCollapsed ? 0.5 : 1 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : filtered.length === 0 ? (
                    !isCollapsed && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                            <Typography variant="caption" color="text.secondary" display="block">
                                {searchQuery ? 'No conversations found' : 'No conversations yet'}
                            </Typography>
                        </Box>
                    )
                ) : (
                    filtered.map((conv) => (
                        <Box
                            key={conv.session_code}
                            onClick={() => editingId !== conv.session_code && handleSelect(conv.session_code)}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                p: isCollapsed ? 1 : 1.5,
                                mb: 0.5,
                                borderRadius: 2,
                                cursor: 'pointer',
                                justifyContent: isCollapsed ? 'center' : 'flex-start',
                                bgcolor:
                                    conv.session_code === currentSessionCode
                                        ? 'primary.lighter'
                                        : 'transparent',
                                '&:hover': {
                                    bgcolor:
                                        conv.session_code === currentSessionCode
                                            ? 'primary.lighter'
                                            : 'action.hover',
                                },
                                '&:hover .conv-actions': { opacity: 1 },
                                transition: 'background-color 0.15s',
                            }}
                        >
                            <ChatBubbleOutlineIcon
                                sx={{
                                    fontSize: 14,
                                    color:
                                        conv.session_code === currentSessionCode
                                            ? 'primary.main'
                                            : 'grey.400',
                                    mt: 0.25,
                                    flexShrink: 0,
                                }}
                            />
                            {!isCollapsed && (
                                <>
                                    {editingId === conv.session_code ? (
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <TextField
                                                size="small"
                                                value={editTitle}
                                                onChange={(e) => setEditTitle(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleRename(conv.session_code)}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                inputProps={{ maxLength: 60, style: { fontSize: '0.75rem', padding: '4px 8px' } }}
                                                sx={{ flex: 1 }}
                                            />
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRename(conv.session_code); }}>
                                                <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                            </IconButton>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingId(null); }}>
                                                <CloseIcon sx={{ fontSize: 14, color: 'grey.500' }} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <>
                                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                <Typography
                                                    variant="caption"
                                                    fontWeight={500}
                                                    noWrap
                                                    sx={{ display: 'block', color: 'text.primary', fontSize: '0.75rem' }}
                                                >
                                                    {conv.title || 'New Conversation'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AccessTimeIcon sx={{ fontSize: 11, color: 'grey.400' }} />
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                        {formatTime(conv.last_message_at)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box
                                                className="conv-actions"
                                                sx={{
                                                    display: 'flex',
                                                    opacity: 0,
                                                    transition: 'opacity 0.15s',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <IconButton size="small" onClick={(e) => startEditing(conv, e)} sx={{ p: 0.25 }}>
                                                    <EditIcon sx={{ fontSize: 12, color: 'grey.500' }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={(e) => handleDelete(conv.session_code, e)} sx={{ p: 0.25 }}>
                                                    <DeleteOutlineIcon sx={{ fontSize: 12, color: 'error.main' }} />
                                                </IconButton>
                                            </Box>
                                        </>
                                    )}
                                </>
                            )}
                        </Box>
                    ))
                )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                    <Avatar
                        sx={{
                            width: 28,
                            height: 28,
                            bgcolor: 'primary.main',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                        }}
                    >
                        {fname?.charAt(0) || user?.user_email?.charAt(0) || 'U'}
                    </Avatar>
                    {!isCollapsed && (
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography variant="caption" fontWeight={500} noWrap display="block">
                                {fullName || user?.user_email || 'User'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    // Mobile: use Drawer
    if (isMobile) {
        return (
            <Drawer anchor="left" open={isOpen} onClose={onToggle} PaperProps={{ sx: { width: 260 } }}>
                {sidebarContent}
            </Drawer>
        );
    }

    // Desktop: inline
    return (
        <Box
            sx={{
                width: isCollapsed ? 56 : 240,
                flexShrink: 0,
                height: '100%',
                transition: 'width 0.3s',
                bgcolor: 'background.paper',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
            }}
        >
            {sidebarContent}
        </Box>
    );
}
